// Marketing attribution store (#596, minimal slice of #576). Captures the
// UTM parameters (and the campaign landing slug) a visitor arrived with, so
// conversion events fired later in the session — free-trial start, purchase —
// can carry the acquisition source (see LoggerContext.logMetricEvent).
//
// Backed by localStorage (key "ff-attribution") as { firstTouch, lastTouch },
// each { params, capturedAt }. First-touch is written once and preserved so
// the original acquisition source survives later revisits; last-touch always
// updates. Records expire on read after 90 days. All storage access is
// wrapped in try/catch (private mode / storage disabled) — attribution is
// best-effort and must never break the page (model: returnPath.ts).

const STORAGE_KEY = "ff-attribution";

const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];

export type AttributionParams = { [key: string]: string };

type AttributionTouch = {
  params: AttributionParams;
  capturedAt: string; // ISO
};

type StoredAttribution = {
  firstTouch: AttributionTouch;
  lastTouch: AttributionTouch;
};

const readStored = (): StoredAttribution | undefined => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return undefined;
    const stored = JSON.parse(raw) as StoredAttribution;
    if (!stored || !stored.lastTouch || !stored.lastTouch.params) {
      return undefined;
    }
    return stored;
  } catch (e) {
    // Corrupt JSON or storage disabled — fail closed, never crash the page.
    return undefined;
  }
};

export const captureAttribution = (
  search: string,
  campaignSlug?: string,
): void => {
  const params: AttributionParams = {};

  const query = new URLSearchParams(search);
  UTM_KEYS.forEach((key) => {
    const value = query.get(key);
    if (value) {
      params[key] = value;
    }
  });

  if (campaignSlug) {
    params.campaign = campaignSlug;
  }

  // No UTMs and no slug: nothing to attribute — never write an empty record.
  if (Object.keys(params).length === 0) {
    return;
  }

  const touch: AttributionTouch = {
    params,
    capturedAt: new Date().toISOString(),
  };

  const existing = readStored();
  const record: StoredAttribution = {
    firstTouch: existing ? existing.firstTouch : touch,
    lastTouch: touch,
  };

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch (e) {
    // Storage disabled — a lost attribution is never fatal.
  }
};

const readValidStored = (): StoredAttribution | undefined => {
  const stored = readStored();
  if (!stored) return undefined;

  // Expire on read: a record older than 90 days no longer attributes, and
  // the dead record is deleted so it never lingers in storage.
  const capturedAt = Date.parse(stored.lastTouch.capturedAt);
  if (Number.isNaN(capturedAt) || Date.now() - capturedAt > MAX_AGE_MS) {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // Storage disabled — nothing to clean up anyway.
    }
    return undefined;
  }

  return stored;
};

export const getAttribution = (): { params: AttributionParams } | undefined => {
  const stored = readValidStored();
  if (!stored) return undefined;

  const params: AttributionParams = { ...stored.lastTouch.params };
  const firstSource =
    stored.firstTouch &&
    stored.firstTouch.params &&
    stored.firstTouch.params.utm_source;
  if (firstSource) {
    params.first_utm_source = firstSource;
  }

  return { params };
};

export type AttributionTouchPayload = AttributionParams & {
  capturedAt?: string;
};

export type AttributionTouchesPayload = {
  firstTouch?: AttributionTouchPayload;
  lastTouch?: AttributionTouchPayload;
};

// Both stored touches, flattened for the application create + submit
// requests (#660): the API persists them on the application row, which is
// what makes the campaign→member funnel queryable server-side. Same 90-day
// expiry as getAttribution; undefined when nothing valid is stored.
export const getAttributionTouches = ():
  AttributionTouchesPayload | undefined => {
  const stored = readValidStored();
  if (!stored) return undefined;

  const flatten = (
    touch?: AttributionTouch,
  ): AttributionTouchPayload | undefined =>
    touch && touch.params
      ? { ...touch.params, capturedAt: touch.capturedAt }
      : undefined;

  return {
    firstTouch: flatten(stored.firstTouch),
    lastTouch: flatten(stored.lastTouch),
  };
};
